import {generateClient, GraphQLResult} from 'aws-amplify/api';
import {
  signUp as signUpAmplify,
  SignUpOutput,
  SignInOutput,
} from 'aws-amplify/auth';
import { UserService } from '@PikaDoApp/npm-pikado-logic';
import {validatePartnerAuthUser} from '../../graphql/mutations';
import {AuthenticatorResponse} from '../../graphql/API';
import {
  getPartnerCognitoId,
} from '../../redux/actions/user';
import { initializePikadoClient } from '../initializePikadoClient';
import { decodeToken } from 'react-jwt';
import { mapSchoolLogoToPromo } from '../mapSchoolLogoToPromo';
const client = generateClient();
const userService = new UserService();

const getEntityId = (entity: any): string | undefined =>
  entity?.id ?? entity?._id;

const getClassSchoolId = (student: any): string | undefined =>
  student?.classSchoolId ?? student?.classschool?.id ?? student?.classschool?._id;

const mapStudentForClass = (student: any, country?: string) => ({
  id: getEntityId(student),
  name: student.name,
  age: student.age,
  birthDate: student.birthDate,
  gender: student.gender,
  user: {
    id: student.userId,
    country: student.user?.country || country || 'SA',
  },
  statistic: {
    id: getEntityId(student),
    averageScore: 0,
  },
  __typename: 'Student',
});
export interface IAuthRepository {
  signIn(userName: string, password: string): Promise<SignInOutput | any>;
  signOut(): Promise<any>;
  signUp(email: string, password: string): Promise<SignUpOutput | Error>;
  validatePartnerAuth(
    partnerAppName: string,
    teacherId: string,
  ): Promise<AuthenticatorResponse>;

  getCurrentUser(): Promise<any>;
}

function parseJwt(token: string) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window.atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Failed to parse JWT natively:', e);
    return null;
  }
}

export class AuthRepository implements IAuthRepository {
  async getCurrentUser(): Promise<any> {
    console.log('ENTER_AUTH_REPOSITORY');

    try {
      let apiResponse: any;

      // Get user from partner auth or from Keycloak session
      if (getPartnerCognitoId()) {
        const cognitoUserId = getPartnerCognitoId();
        console.log('Fetching user by cognitoUserId (partner):', cognitoUserId);
        apiResponse = await userService.getUserByCognitoUserId(cognitoUserId);
      } else {
        const token = localStorage.getItem('accessToken');
        if (!token || token === 'undefined' || token === 'null') {
          throw new Error('No access token found');
        }
        
        let decoded: any = null;
        try {
          decoded = decodeToken(token);
        } catch (e) {
          console.error('react-jwt decode failed, falling back to native decode:', e);
        }

        if (!decoded) {
          decoded = parseJwt(token);
        }

        const keycloakId = decoded?.sub;
        const email = decoded?.email;

        if (keycloakId) {
          console.log('Fetching user by keycloakId:', keycloakId);
          apiResponse = await userService.getUserByKeycloakId(keycloakId);
        } else if (email) {
          console.log('Fetching user by email (fallback):', email);
          apiResponse = await userService.getUserByEmail(email);
        } else {
          console.error('Decoded token payload:', decoded);
          throw new Error('Invalid token: both sub and email claims are missing');
        }
      }

      console.log('getUserByCognitoUserId/getUserByKeycloakId response:', JSON.stringify(apiResponse, null, 2));

      // Fetch classes if school profile exists OR if teacher has a school
      let classesData = [];
      let schoolIdToFetch = null;

      // Determine which schoolId to use for fetching classes
      if (apiResponse.schoolProfile?._id) {
        schoolIdToFetch = apiResponse.schoolProfile._id;
      } else if (apiResponse.teacherProfile?.schoolId) {
        schoolIdToFetch = apiResponse.teacherProfile.schoolId;
      }

      if (schoolIdToFetch) {
        try {
          console.log('Fetching classes for schoolId:', schoolIdToFetch);
          const classesResponse = await userService.getClassSchoolsBySchoolId(schoolIdToFetch);
          console.log('getClassSchoolsBySchoolId response:', JSON.stringify(classesResponse, null, 2));

          console.log('Fetching students for schoolId:', schoolIdToFetch);
          let studentsResponse = [];
          try {
            studentsResponse = await userService.getStudentsBySchoolId(schoolIdToFetch);
          } catch (studentsErr) {
            console.error('Error fetching students:', studentsErr);
          }

          // Transform classes to match the expected structure
          classesData = classesResponse.map((classItem: any) => {
            const classId = getEntityId(classItem);
            const classStudents = studentsResponse
              .filter((s: any) => getClassSchoolId(s) === classId)
              .map((s: any) => mapStudentForClass(s, apiResponse.country));

            return {
              id: classId,
              name: classItem.name,
              nameArabic: classItem.nameArabic,
              grade: classItem.grade,
              createdAt: classItem.createdAt,
              updatedAt: classItem.updatedAt,
              teacherPrograms: classItem.teacherPrograms || [], // Include teacher assignments
              schoolId: classItem.schoolId,
              students: classStudents,
              level: classItem.levelId ? {
                id: classItem.levelId,
                number: 1,
                __typename: 'Level',
              } : null,
              statistic: {
                id: classItem._id,
                averageScore: 0,
                __typename: 'Classstatistic',
              },
              __typename: 'Classschool',
            };
          });

          console.log('Transformed classesData with teacherPrograms:',
            classesData.map((c: any) => ({
              id: c.id,
              name: c.name,
              teacherPrograms: c.teacherPrograms
            }))
          );
        } catch (classError) {
          console.error('Error fetching classes:', classError);
          // Continue with empty classes if fetch fails
        }
      }

      // Fetch school details if teacher has schoolId but no schoolProfile in response
      let schoolData = null;
      if (apiResponse.teacherProfile?.schoolId && !apiResponse.schoolProfile) {
        try {
          console.log('Fetching school details for teacher, schoolId:', apiResponse.teacherProfile.schoolId);
          const schoolResponse = await userService.getSchoolById(apiResponse.teacherProfile.schoolId);
          console.log('getSchoolById response:', JSON.stringify(schoolResponse, null, 2));
          schoolData = schoolResponse;
        } catch (schoolError) {
          console.error('Error fetching school:', schoolError);
        }
      }

      // Determine which school data to use (from direct schoolProfile or fetched schoolData)
      const effectiveSchoolProfile = apiResponse.schoolProfile || schoolData;

      // Transform the new API response to match the old structure expected by Redux
      const transformedUser = {
        id: apiResponse._id,
        cognitoUserId: apiResponse.cognitoUserId,
        whatsappNumber: apiResponse.whatsappNumber,
        country: apiResponse.country,
        timeZone: apiResponse.timeZone,
        createdAt: apiResponse.createdAt,
        updatedAt: apiResponse.updatedAt,

        // Map school profile
        school: effectiveSchoolProfile ? {
          id: effectiveSchoolProfile._id,
          name: effectiveSchoolProfile.name,
          nameArabic: effectiveSchoolProfile.nameArabic,
          createdAt: effectiveSchoolProfile.createdAt,
          updatedAt: effectiveSchoolProfile.updatedAt,
          promo: mapSchoolLogoToPromo(effectiveSchoolProfile.logoUrl),
          classesschool: classesData,
          students: classesData.flatMap((classItem: any) => classItem.students || []),
          __typename: 'School',
        } : null,

        // Map teacher profile
        teacher: apiResponse.teacherProfile ? {
          id: apiResponse.teacherProfile._id,
          name: apiResponse.teacherProfile.name,
          gender: apiResponse.teacherProfile.gender,
          profilePicture: apiResponse.teacherProfile.profilePictureUrl ? {
            fileUri: apiResponse.teacherProfile.profilePictureUrl,
            thumbnailUri: apiResponse.teacherProfile.profilePictureUrl,
          } : null,
          classesschool: classesData,
          school: effectiveSchoolProfile ? {
            id: effectiveSchoolProfile._id,
            name: effectiveSchoolProfile.name,
            nameArabic: effectiveSchoolProfile.nameArabic,
            createdAt: effectiveSchoolProfile.createdAt,
            updatedAt: effectiveSchoolProfile.updatedAt,
            promo: mapSchoolLogoToPromo(effectiveSchoolProfile.logoUrl),
            __typename: 'School',
          } : null,
          __typename: 'Teacher',
        } : null,
      };

      console.log('Transformed user:', JSON.stringify(transformedUser, null, 2));

      return transformedUser;
    } catch (error) {
      console.error('Error in getCurrentUser:', error);
      throw error;
    }
  }

  async signIn(
    userName: string,
    password: string,
  ): Promise<any> {
    const response = await userService.login({ email: userName, password: password });

    // Store tokens
    localStorage.setItem('accessToken', response.token);
    localStorage.setItem('refreshToken', response.refreshToken);

    // Initialize Pikado client config with the new tokens immediately
    initializePikadoClient();

    return response;
  }

  async validatePartnerAuth(
    partnerAppName: string,
    teacherId: string,
  ): Promise<AuthenticatorResponse> {
    let token: GraphQLResult<any>;
    token = await client.graphql({
      query: validatePartnerAuthUser,
      variables: {
        input: {
          partnerId: teacherId,
          partnerAppName,
        },
      },
      authToken: 'partner_auth-' + partnerAppName,
    });
    return token.data.validatePartnerAuthUser;
  }

  async signOut(): Promise<any> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      try {
        await userService.signOut(refreshToken);
      } catch (err) {
        console.error('Error signing out from pikado logic:', err);
      }
    }
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  async signUp(
    userName: string,
    password: string,
  ): Promise<SignUpOutput | Error> {
    return await signUpAmplify({
      username: userName,
      password: password,
    });
  }
}
